import React, { useRef, useState } from 'react';
import { Globe, ArrowRight, Loader2, Info } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { CountryCombobox } from './ui/country-combobox';
import { quotesAPI } from '../services/api';
import { carrierLogo } from '../utils/carrierLogo';
import { carrierDisplayName } from '../lib/quoteBadges';
import { zoneFieldFor } from '../lib/destinationZones';
import { useToast } from '../hooks/use-toast';

// Reference weight used to show "starting from" per-carrier rates before the
// customer has entered their parcel details. The exact price is recomputed from
// the real weight/dimensions in the booking step.
const SAMPLE_WEIGHT = 0.5;

const nameOf = (c) => c?.name || c?.countryName || '';
const codeOf = (c) => c?.code || c?.countryCode || '';

const QuoteExplorer = ({ priorityCountries = [], loadingCountries, onProceed }) => {
  const [countryCode, setCountryCode] = useState('');
  const [rates, setRates] = useState(null);
  const [loadingRates, setLoadingRates] = useState(false);
  const [selectedProvider, setSelectedProvider] = useState(null);
  // Rates for the customer's own parcel, not a reference one. Blank falls back
  // to SAMPLE_WEIGHT so the page still shows something before they type.
  const [weight, setWeight] = useState('');
  const [postcode, setPostcode] = useState('');
  const weightTimer = useRef(null);
  const { toast } = useToast();

  const selectedCountry = priorityCountries.find((c) => codeOf(c) === countryCode);

  const effectiveWeight = parseFloat(weight) > 0 ? parseFloat(weight) : SAMPLE_WEIGHT;

  const loadRates = async (code, kg, pc = postcode) => {
    setRates(null);
    setSelectedProvider(null);
    const country = priorityCountries.find((c) => codeOf(c) === code);
    if (!country) return;

    setLoadingRates(true);
    try {
      const resp = await quotesAPI.getPriorityConnectionsRates(nameOf(country), kg, 'Parcel', pc);
      const list = (resp.rates || [])
        .map((r) => ({
          provider: r.Provider || r.provider || 'Carrier',
          rate: parseFloat(r.Rate ?? r.rate ?? 0) || 0,
          zone: r.Zone || r.zone || '',
        }))
        .filter((r) => r.rate > 0)
        .sort((a, b) => a.rate - b.rate);
      setRates(list);
      if (list.length) setSelectedProvider(list[0].provider); // preselect cheapest
    } catch (e) {
      console.error('Error loading rates:', e);
      toast({
        title: 'Could not load rates',
        description: 'Please try another destination or retry in a moment.',
        variant: 'destructive',
      });
      setRates([]);
    } finally {
      setLoadingRates(false);
    }
  };

  const handleCountryChange = (code) => {
    setCountryCode(code);
    // A Melbourne postcode means nothing once the destination is Canada
    setPostcode('');
    loadRates(code, effectiveWeight, '');
  };

  const handlePostcodeChange = (value) => {
    setPostcode(value);
    if (!countryCode) return;
    clearTimeout(weightTimer.current);
    weightTimer.current = setTimeout(() => loadRates(countryCode, effectiveWeight, value), 600);
  };

  // Re-price when the customer settles on a weight, rather than on each keystroke.
  const handleWeightChange = (value) => {
    setWeight(value);
    if (!countryCode) return;
    clearTimeout(weightTimer.current);
    weightTimer.current = setTimeout(() => {
      const kg = parseFloat(value) > 0 ? parseFloat(value) : SAMPLE_WEIGHT;
      loadRates(countryCode, kg, postcode);
    }, 600);
  };

  const handleProceed = () => {
    const chosen = rates?.find((r) => r.provider === selectedProvider);
    if (!selectedCountry || !chosen) return;
    onProceed({
      carrier_name: chosen.provider,
      total_cost: chosen.rate,
      // Send the weight the rate was actually priced on, so the booking step
      // can tell whether the parcel still matches the price. Only a weight the
      // customer typed is carried into their box; the SAMPLE_WEIGHT fallback is
      // a reference they never chose, and prefilling it would let a 0.5 kg
      // booking be clicked through for a parcel that is nothing of the sort.
      chargeable_weight: effectiveWeight,
      to_postal_code: postcode || null,
      ...(parseFloat(weight) > 0 ? { weight: parseFloat(weight) } : {}),
      to_country: codeOf(selectedCountry),
      to_country_name: nameOf(selectedCountry),
      shipment_type: 'parcel',
    });
  };

  return (
    <Card className="bg-white shadow-xl border-0">
      <CardHeader>
        <CardTitle className="text-2xl font-bold text-slate-900 flex items-center">
          <Globe className="mr-3 h-6 w-6 text-orange-500" />
          Get an Instant Shipping Quote
        </CardTitle>
        <p className="text-slate-600">
          Pick a destination and enter your parcel weight to compare carriers. You&apos;ll add
          dimensions &amp; address details on the next step.
        </p>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Destination country */}
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-2">Destination country</label>
          {/* Same type-ahead the homepage uses: 200-odd countries is too many
              to scroll, and the customer usually knows the name already. */}
          <CountryCombobox
            countries={priorityCountries}
            value={countryCode}
            onChange={handleCountryChange}
            loading={loadingCountries}
            placeholder={loadingCountries ? 'Loading destinations…' : 'Search a country'}
          />
        </div>

        {/* Weight: without it the page could only ever quote a reference parcel,
            which read as the price and was not one. */}
        <div>
          <label htmlFor="quote-weight" className="block text-sm font-medium text-slate-700 mb-2">
            Weight (kg)
          </label>
          <input
            id="quote-weight"
            type="number"
            inputMode="decimal"
            min="0.1"
            step="0.5"
            value={weight}
            onChange={(e) => handleWeightChange(e.target.value)}
            placeholder={`e.g. ${SAMPLE_WEIGHT}`}
            className="w-full h-10 rounded-md border border-slate-200 px-3 text-sm focus:outline-none focus:ring-2 focus:ring-orange-400"
          />
        </div>

        {zoneFieldFor(countryCode) && (
          <div>
            <label htmlFor="quote-postcode" className="block text-sm font-medium text-slate-700 mb-2">
              {zoneFieldFor(countryCode).label}
            </label>
            {zoneFieldFor(countryCode).options ? (
              <CountryCombobox
                id="quote-postcode"
                countries={zoneFieldFor(countryCode).options.map(([code, name]) => ({ code, name }))}
                value={postcode}
                onChange={(code) => handlePostcodeChange(code)}
                placeholder="Search a state"
              />
            ) : (
              <input
                id="quote-postcode"
                type="text"
                inputMode={zoneFieldFor(countryCode).mode}
                maxLength={zoneFieldFor(countryCode).maxLength}
                autoComplete="postal-code"
                value={postcode}
                onChange={(e) => handlePostcodeChange(e.target.value)}
                placeholder={zoneFieldFor(countryCode).placeholder}
                className="w-full h-10 rounded-md border border-slate-200 px-3 text-sm focus:outline-none focus:ring-2 focus:ring-orange-400"
              />
            )}
            <p className="mt-1 text-xs text-slate-400">
              {zoneFieldFor(countryCode).hint}
            </p>
          </div>
        )}

        {/* Loading rates */}
        {loadingRates && (
          <div className="flex items-center justify-center py-10 text-slate-500">
            <Loader2 className="h-6 w-6 animate-spin mr-2" /> Fetching carrier rates…
          </div>
        )}

        {/* No rates */}
        {!loadingRates && rates && rates.length === 0 && countryCode && (
          <div className="text-center py-8 text-slate-500">
            No carrier rates are available for this destination right now. Please try another country.
          </div>
        )}

        {/* Rate cards */}
        {!loadingRates && rates && rates.length > 0 && (
          <div className="space-y-4">
            <div className="grid gap-3 sm:grid-cols-2">
              {rates.map((r, i) => {
                const active = selectedProvider === r.provider;
                const logo = carrierLogo(r.provider);
                return (
                  <button
                    key={`${r.provider}-${i}`}
                    type="button"
                    onClick={() => setSelectedProvider(r.provider)}
                    className={`text-left rounded-xl border p-4 transition-all ${
                      active
                        ? 'border-orange-500 ring-2 ring-orange-100 bg-orange-50/40'
                        : 'border-gray-200 hover:border-orange-300'
                    }`}
                  >
                    <div className="flex items-center justify-between gap-2">
                      <div className="flex items-center gap-2 min-w-0">
                        {logo && (
                          <img
                            src={logo}
                            alt={r.provider}
                            className="h-6 w-auto max-w-[64px] object-contain flex-shrink-0"
                          />
                        )}
                        <span className="font-semibold text-slate-900 truncate">{carrierDisplayName(r.provider)}</span>
                      </div>
                      {i === 0 && (
                        <span className="text-[10px] font-bold uppercase tracking-wide text-green-700 bg-green-100 rounded-full px-2 py-0.5 flex-shrink-0">
                          Best price
                        </span>
                      )}
                    </div>
                    <div className="mt-2 text-2xl font-bold text-slate-900">
                      ₹{r.rate.toLocaleString('en-IN')}
                    </div>
                    <div className="text-xs text-slate-500 mt-0.5">
                      · {effectiveWeight} kg{r.zone ? ` · Zone ${r.zone}` : ''}
                    </div>
                  </button>
                );
              })}
            </div>

            <div className="flex items-start gap-2 text-xs text-slate-500 bg-slate-50 rounded-lg p-3">
              <Info className="h-4 w-4 flex-shrink-0 mt-0.5" />
              <span>
                {parseFloat(weight) > 0
                  ? `Rates for ${effectiveWeight} kg. Your final price is confirmed from the actual`
                  : `These are starting rates for ${SAMPLE_WEIGHT} kg. Your final price is calculated from the actual`}
                weight and dimensions you enter in the booking step.
              </span>
            </div>

            <div className="flex justify-end">
              <Button
                onClick={handleProceed}
                disabled={!selectedProvider}
                className="bg-orange-500 hover:bg-orange-600 text-white"
              >
                Proceed to booking
                <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default QuoteExplorer;
